// 📄 pages/Proyectos/components/HitosSection.tsx
// "Hitos y avance" (plan-proyectos-generales.md, G4): fases o entregables
// del proyecto con fecha objetivo, estado y responsable, y el avance general
// (hitos completados / total). Cada hito muestra sus tareas asociadas y deja
// crear una ya vinculada o completarlas ahí mismo. Reglas acordadas:
//   - completar un hito con tareas abiertas pide confirmación (no bloquea);
//   - si todas sus tareas están listas, se sugiere completarlo;
//   - una tarea que arranca o se completa pone el hito En curso (tareaService).
// El rol cliente lo ve en solo lectura (salvo completar sus propias tareas).
import React, { useEffect, useMemo, useState } from 'react';
import { Flag, Plus, RefreshCw, Pencil, Trash2, ChevronDown, ChevronUp, Calendar, User, X, Save, ListTodo, CheckCircle2 } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import { ProjectMember } from '../../../services/projectService.ts';
import { getTareas, Tarea, updateTareaEstado } from '../../../services/tareaService.ts';
import {
  Hito, HitoEstado, HitoFormData, ESTADOS_HITO, getHitos, createHito, updateHitoDatos, updateHitoEstado, deleteHito,
  hitoAtrasado, porcentajeAvance
} from '../../../services/hitoService.ts';
import { getDisplayName } from '../../../utils/displayName.ts';
import TareaFormModal from './TareaFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

const ESTADO_STYLES: Record<HitoEstado, string> = {
  Pendiente: 'text-gray-300 bg-white/5 border-white/15',
  'En curso': 'text-blue-300 bg-blue-500/10 border-blue-500/30',
  Completado: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
};

function BarraAvance({ porcentaje, alto = 'h-2' }: { porcentaje: number; alto?: string }) {
  return (
    <div className={`w-full ${alto} bg-white/10 rounded-full overflow-hidden`}>
      <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${porcentaje}%` }} />
    </div>
  );
}

export default function HitosSection() {
  const { project, projectId, esCliente } = useProject();
  const { user, profile } = useAuth();
  const [hitos, setHitos] = useState<Hito[]>([]);
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formHito, setFormHito] = useState<Hito | 'nuevo' | null>(null);
  const [borrando, setBorrando] = useState<Hito | null>(null);
  const [tareaParaHito, setTareaParaHito] = useState<string | null>(null);
  const [abiertos, setAbiertos] = useState<Record<string, boolean>>({});
  // Completar un hito con tareas abiertas: se confirma antes (no se bloquea).
  const [confirmarCierre, setConfirmarCierre] = useState<{ hito: Hito; pendientes: number } | null>(null);
  const [tareaGuardando, setTareaGuardando] = useState<string | null>(null);

  const miembros: ProjectMember[] = (project?.miembros || []).filter((m) => m.activo !== false);

  const cargar = async () => {
    setLoading(true);
    try {
      const [h, t] = await Promise.all([getHitos(projectId), getTareas(projectId)]);
      setHitos(h);
      setTareas(t);
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar los hitos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const tareasPorHito = useMemo(() => {
    const mapa: Record<string, Tarea[]> = {};
    for (const t of tareas) if (t.hitoId) (mapa[t.hitoId] = mapa[t.hitoId] || []).push(t);
    return mapa;
  }, [tareas]);

  const completados = hitos.filter((h) => h.estado === 'Completado').length;
  const porcentaje = porcentajeAvance({ completados, total: hitos.length });
  const atrasados = hitos.filter(hitoAtrasado).length;

  const cambiarEstado = async (hito: Hito, estado: HitoEstado, confirmado = false) => {
    setError(null);
    const pendientes = (tareasPorHito[hito.id] || []).filter((t) => t.estado !== 'Completada').length;
    if (estado === 'Completado' && pendientes > 0 && !confirmado) {
      setConfirmarCierre({ hito, pendientes });
      return;
    }
    try {
      await updateHitoEstado(projectId, hito.id, estado);
      setHitos((prev) => prev.map((h) => (h.id === hito.id ? { ...h, estado } : h)));
    } catch (err: any) {
      setError(err.message || 'No se pudo cambiar el estado.');
    }
  };

  /** Completar o reabrir una tarea desde el hito (el cliente, solo las suyas). */
  const alternarTarea = async (t: Tarea, hito: Hito) => {
    const nuevo = t.estado === 'Completada' ? 'Pendiente' : 'Completada';
    setTareaGuardando(t.id);
    setError(null);
    try {
      await updateTareaEstado(projectId, t.id, nuevo, hito.id);
      setTareas((prev) => prev.map((x) => (x.id === t.id ? { ...x, estado: nuevo } : x)));
      if (nuevo === 'Completada' && hito.estado === 'Pendiente' && !esCliente) {
        setHitos((prev) => prev.map((h) => (h.id === hito.id ? { ...h, estado: 'En curso' } : h)));
      }
    } catch (err: any) {
      setError(err.message || 'No se pudo actualizar la tarea.');
    } finally {
      setTareaGuardando(null);
    }
  };

  const confirmarBorrado = async () => {
    if (!borrando) return;
    try {
      await deleteHito(projectId, borrando.id);
      setBorrando(null);
      cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo eliminar el hito.');
    }
  };

  if (!project || !user) return null;

  return (
    <div className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Avance del proyecto</p>
            <p className="text-3xl font-light text-white mt-1">{porcentaje}%</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {hitos.length ? `${completados} de ${hitos.length} hitos completados` : 'Todavía no hay hitos'}
              {atrasados > 0 && <span className="text-red-300"> · {atrasados} atrasado{atrasados > 1 ? 's' : ''}</span>}
            </p>
          </div>
          {!esCliente && (
            <button
              onClick={() => setFormHito('nuevo')}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
            >
              <Plus className="w-4 h-4" /> Nuevo hito
            </button>
          )}
        </div>
        <BarraAvance porcentaje={porcentaje} />
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando hitos...</p>
        </div>
      ) : hitos.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Flag className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Define las fases o entregables del proyecto (ej. Análisis, Desarrollo, Salida a producción) para seguir su avance.</p>
        </div>
      ) : (
        <ol className="space-y-3">
          {hitos.map((h, i) => {
            const suyas = tareasPorHito[h.id] || [];
            const hechas = suyas.filter((t) => t.estado === 'Completada').length;
            const atrasado = hitoAtrasado(h);
            // Las tareas se ven abiertas mientras el hito no está completado.
            const abierto = abiertos[h.id] ?? h.estado !== 'Completado';
            return (
              <li key={h.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${h.estado === 'Completado' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-gray-300'}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="text-sm font-semibold text-white">{h.nombre}</h4>
                      {esCliente ? (
                        <span className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ESTADO_STYLES[h.estado]}`}>{h.estado}</span>
                      ) : (
                        <select
                          value={h.estado}
                          onChange={(e) => cambiarEstado(h, e.target.value as HitoEstado)}
                          className={`flex-shrink-0 text-[11px] font-semibold px-2 py-1 rounded-lg border bg-transparent focus:outline-none ${ESTADO_STYLES[h.estado]}`}
                        >
                          {ESTADOS_HITO.map((e) => <option key={e} value={e} className="bg-zinc-800 text-white">{e}</option>)}
                        </select>
                      )}
                    </div>
                    {h.descripcion && <p className="text-xs text-gray-400 mt-1 whitespace-pre-wrap">{h.descripcion}</p>}
                    <div className="flex items-center gap-3 flex-wrap mt-2 text-xs">
                      <span className={`flex items-center gap-1 ${atrasado ? 'text-red-300' : 'text-gray-400'}`}>
                        <Calendar className="w-3 h-3" /> {h.fechaObjetivo || 'Sin fecha'}{atrasado ? ' · Atrasado' : ''}
                      </span>
                      <span className="flex items-center gap-1 text-gray-400">
                        <User className="w-3 h-3" /> {h.responsableNombre || 'Sin responsable'}
                      </span>
                      <button type="button" onClick={() => setAbiertos((p) => ({ ...p, [h.id]: !abierto }))} className="flex items-center gap-1 text-gray-400 hover:text-white">
                        <ListTodo className="w-3 h-3" /> {suyas.length ? `${hechas}/${suyas.length} tareas` : 'Sin tareas'}
                        {suyas.length > 0 && (abierto ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                      </button>
                    </div>
                    {suyas.length > 0 && <div className="mt-2"><BarraAvance porcentaje={Math.round((hechas / suyas.length) * 100)} alto="h-1" /></div>}
                  </div>
                </div>

                {abierto && suyas.length > 0 && (
                  <ul className="ml-10 space-y-1">
                    {suyas.map((t) => {
                      const puede = !esCliente || t.asignadoA === user.uid;
                      return (
                        <li key={t.id} className="flex items-center justify-between gap-2 text-xs bg-black/20 rounded-lg px-3 py-1.5">
                          <label className={`flex items-center gap-2 min-w-0 ${puede ? 'cursor-pointer' : ''}`}>
                            <input
                              type="checkbox"
                              checked={t.estado === 'Completada'}
                              disabled={!puede || tareaGuardando === t.id}
                              onChange={() => alternarTarea(t, h)}
                              className="accent-emerald-500 w-3.5 h-3.5 flex-shrink-0"
                              title={puede ? 'Marcar como completada' : 'Solo quien la tiene asignada o el equipo puede cambiarla'}
                            />
                            <span className={`truncate ${t.estado === 'Completada' ? 'text-gray-500 line-through' : 'text-gray-200'}`}>{t.titulo}</span>
                          </label>
                          <span className="text-gray-500 whitespace-nowrap">{t.asignadoANombre} · {t.estado}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {!esCliente && suyas.length > 0 && hechas === suyas.length && h.estado !== 'Completado' && (
                  <div className="ml-10 flex items-center justify-between gap-3 flex-wrap text-xs bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 rounded-lg px-3 py-2">
                    <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Todas las tareas de este hito están completadas.</span>
                    <button type="button" onClick={() => cambiarEstado(h, 'Completado')} className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-[#fff] font-semibold">
                      Marcar hito como completado
                    </button>
                  </div>
                )}

                {!esCliente && (
                  <div className="flex items-center justify-end gap-1 ml-10">
                    <button type="button" onClick={() => setTareaParaHito(h.id)} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/10">
                      <Plus className="w-3.5 h-3.5" /> Tarea
                    </button>
                    <button type="button" onClick={() => setFormHito(h)} title="Editar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => setBorrando(h)} title="Eliminar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {formHito && (
        <HitoFormModal
          projectId={projectId}
          miembros={miembros}
          hito={formHito === 'nuevo' ? null : formHito}
          creadoPor={user.uid}
          onClose={() => setFormHito(null)}
          onSaved={() => { setFormHito(null); cargar(); }}
        />
      )}

      {tareaParaHito && (
        <TareaFormModal
          projectId={projectId}
          proyectoNombre={project.nombre}
          miembros={miembros}
          creadoPor={user.uid}
          creadoPorNombre={getDisplayName(user, profile)}
          hitos={hitos}
          hitoInicial={tareaParaHito}
          onClose={() => setTareaParaHito(null)}
          onSaved={() => { setTareaParaHito(null); setAbiertos((p) => ({ ...p, [tareaParaHito]: true })); cargar(); }}
        />
      )}

      {confirmarCierre && (
        <ConfirmDialog
          title="¿Completar el hito con tareas pendientes?"
          message={`"${confirmarCierre.hito.nombre}" tiene ${confirmarCierre.pendientes} tarea${confirmarCierre.pendientes > 1 ? 's' : ''} sin completar. Si ya no aplican, puedes completarlo igual.`}
          confirmLabel="Completar igual"
          onConfirm={() => { const c = confirmarCierre; setConfirmarCierre(null); cambiarEstado(c.hito, 'Completado', true); }}
          onCancel={() => setConfirmarCierre(null)}
        />
      )}

      {borrando && (
        <ConfirmDialog
          title="¿Eliminar este hito?"
          message={`Se eliminará "${borrando.nombre}". Sus tareas no se borran: quedan sin hito.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={confirmarBorrado}
          onCancel={() => setBorrando(null)}
        />
      )}
    </div>
  );
}

function HitoFormModal({ projectId, miembros, hito, creadoPor, onClose, onSaved }: {
  projectId: string;
  miembros: ProjectMember[];
  hito: Hito | null;
  creadoPor: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<HitoFormData>(
    hito
      ? { nombre: hito.nombre, descripcion: hito.descripcion, fechaObjetivo: hito.fechaObjetivo, responsableUid: hito.responsableUid }
      : { nombre: '', descripcion: '', fechaObjetivo: '', responsableUid: '' }
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      if (hito) await updateHitoDatos(projectId, hito.id, form, miembros);
      else await createHito(projectId, form, miembros, creadoPor);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'No se pudo guardar el hito.');
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{hito ? 'Editar hito' : 'Nuevo hito'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}
          <div>
            <label className={labelClass}>Nombre*</label>
            <input autoFocus value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} maxLength={120} placeholder="ej. Salida a producción" className={inputClass} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Fecha objetivo</label>
              <input type="date" value={form.fechaObjetivo} onChange={(e) => setForm({ ...form, fechaObjetivo: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Responsable</label>
              <select value={form.responsableUid} onChange={(e) => setForm({ ...form, responsableUid: e.target.value })} className={inputClass}>
                <option value="">Sin responsable</option>
                {miembros.map((m) => <option key={m.userId} value={m.userId}>{m.email}{m.rol === 'cliente' ? ' (cliente)' : ''}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelClass}>Descripción</label>
            <textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={3} maxLength={2000} className={`${inputClass} resize-none`} placeholder="Qué incluye o qué se entrega en este hito..." />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
            <button type="submit" disabled={guardando} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white">
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
